import { makeStyles } from "@material-ui/core";
import { formatISO, parseISO } from "date-fns";
import { IBillingValues } from "../types/locationTypes";

const createBillingValuesObj = (): IBillingValues => ({
  annualInflationIncrease: null,
  commencementDate: null,
  transferFees: null,
  weekDayThreeTransferFees: null,
  weekDayFourTransferFees: null,
  weekEndThreeTransferFees: null,
  weekEndFourTransferFees: null,
  week20TransfersFees: null,
  week25TransfersFees: null,
  week30TransfersFees: null,
  week36OverTransfersFees: null,
  week36TransfersFees: null,
  perTransferFees: null,
  freeDistance: null,
  perKmFees: null,
  feeCap: null,
  applyCancellationFees: null,
  cancellationPeriod: null,
  hasRetainer: null,
  retainerInterval: null,
  retainerTransfersNum: null,
  transferFeesDoubled: null,
  maxDistancePerTransfer: null,
  timeLimitPerTransfer: null,
  addWaitTimeFees: null,
  waitTimeGracePeriod: null,
  waitTimeRatePerHour: null,
  inflationUplifts: [],
  isPrimaryHospital: false,
  billingRuleFrequency: null,
  billingRuleID: null,
  billingRuleVersion: null,
  automatedPricingUnavailableBefore: null,
  contractedEntity: null

});

const convertBillingDataForRequest = (name: string, value: any): any => {
  switch (name) {
    case "commencementDate":
      return value
        ? formatISO(value as Date, { representation: "date" })
        : null;
    case "applyCancellationFees":
    case "hasRetainer":
    case "transferFeesDoubled":
    case "addWaitTimeFees":
      return !!value;
    case "retainerInterval":
      return value === "2" ? 2 : 1; // 1 (weekly), 2 (monthly)
    case "isPrimaryHospital":
      return value;
    case "billingRuleFrequency":
      return value;
    case "billingRuleID":
      return value;
    case "billingRuleVersion":
      return value ? Number(value) : null;
    case "contractedEntity":
        return value;
    case "automatedPricingUnavailableBefore":
      return value
        ? formatISO(value as Date, { representation: "date" })
        : null;
    default:
      return value ? Number(value) : null;
  }
};

const convertBillingDataFromResponse = (name: string, value: any): any => {
  switch (name) {
    case "commencementDate":
      return value ? parseISO(value) : null;
    case "applyCancellationFees":
    case "hasRetainer":
    case "transferFeesDoubled":
    case "addWaitTimeFees":
      return !!value;
    case "retainerInterval":
      return value === "2" ? 2 : 1; // 1 (weekly), 2 (monthly)
    case "isPrimaryHospital":
      return value;
    case "billingRuleFrequency":
      return value;
    case "billingRuleID":
      return value;
    case "billingRuleVersion":
      return value ? Number(value) : null;
    case "contractedEntity":
        return value;
    case "automatedPricingUnavailableBefore":
        return value ? parseISO(value) : null;
    default:
      return value ? value.toString() : null;
  }
};

const prepareBillingDataForRequest = (
  billingValuesObj: any,
  billingType: string
) => {
  const billingTypeNum = Number(billingType);
  let newObj = Object.assign(
    {},
    ...Object.keys(billingValuesObj).map((key) => {
      return {
        [key]: convertBillingDataForRequest(key, billingValuesObj[key]),
      };
    })
  );
  newObj.billingType = billingTypeNum;
  return newObj;
};

const prepareBillingDataFromResponse = (model: any) => {
  let objFromResponse = createBillingValuesObj();
  for (const k in objFromResponse) {
    if (model.hasOwnProperty(k)) {
      const key = k as keyof IBillingValues;
      (objFromResponse[key] as any) = convertBillingDataFromResponse(
        k,
        model[k]
      );
    }
  }
  return objFromResponse;
};

const entryBlockWidth = 230;

const useStylesForForms = makeStyles((theme) => ({
  flexContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  row: {
    display: "flex",
    flexDirection: "column",
  },
  [theme.breakpoints.up("md")]: {
    row: {
      display: "flex",
      flexDirection: "row",
      gap: "30px",
      flexWrap: "wrap",
    },
  },
  formControlLabel: {
    width: entryBlockWidth,
    fontSize: 14,
    color: "#949494",
    fontWeight: 700,
    display: "block",
    marginTop: 12,
    "&.automatedPricing": {
      width: "360px",
    },
    "&.version": {
      width: 180,
    },
  },
  formDescr: {
    fontSize: 18,
    color: "#949494",
    display: "block",
    margin: "12px 0",
  },
  formControlLabelSub: {
    width: entryBlockWidth,
    fontSize: 15,
    color: "#949494",
    fontWeight: 400,
    display: "block",
    "&.automatedPricing": {
      width: "318px",
      color: "#333333",
      fontSize: 14,
      fontWeight: 400,
    },
    "&.version": {
      width: 180,
    },
  },
  textFieldInput: {
    width: entryBlockWidth,
    margin: "10px 0px",
    borderRadius: 0,
    "&.version": {
      width: 180,
    },
  },
  separator: {
    height: 0,
    width: "100%",
    borderTop: "1px solid #979797",
  },
  ramsayRateCard: {
    padding: "12px 0px",
    "& .heading": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "12px",
      "& .title": {
        fontSize: 18,
        fontWeight: 700,
        color: "#333333",
      },
      "& .rightBtn": {
        minWidth: 0,
        height: "36px",
        padding: "0px 24px",
      },
    },
    "& .body": {
      "& .pdfLink": {
        display: "block",
        textDecoration: "none",
        fontSize: 18,
        fontWeight: 700,
        color: "#0049A3",
      },
      "& .tableContainer": {
        "& .tableHeader": {
          padding: "0px 10px",
          "& .tableHead": {
            fontSize: 14,
            fontWeight: 700,
            color: "#949494",
          },
        },
        "& .tableBody": {
          padding: "10px 10px",
          background: "#ECECEC",
          "& .tableDesc": {
            fontSize: 18,
            fontWeight: 700,
            color: "#333333",
          },
          "& .tableAction": {
            color: "#000000",
            textAlign: "right",
            borderLeft: "3px solid #FDFDFD",
            "& .MuiIconButton-root": {
              padding: 0,
            },
          },
        },
        "& .tableFooter": {
          padding: "10px 10px",
          "& .tableDesc": {
            fontSize: 18,
            fontWeight: 700,
            color: "#333333",
            "&.total": {
              fontSize: 16,
              fontWeight: 500,
              color: "#474747",
            },
          },
        },
      },
    },
    "& .checknox": {
      "& .MuiFormControlLabel-root": {
        margin: 0,
        "& .MuiFormControlLabel-label": {
          fontSize: 18,
          fontWeight: 700,
          color: "#333333",
        },
      },
      "& .hint": {
        fontSize: 14,
        fontWeight: 400,
        color: "#949494",
        margin: "8px 0px 0px 40px",
      },
    },
  },
  upliftDialog: {
    "& .MuiDialogTitle-root": {
      fontSize: 18,
      fontWeight: 500,
      color: "#161C1F",
      margin: 0,
      padding: theme.spacing(3),
      "& .MuiIconButton-root": {
        padding: 0,
        margin: 0,
      },
      "& .closeButton": {
        float: "right",
      },
    },
    "& .MuiDialogContent-root": {
      margin: 0,
      padding: theme.spacing(3),
      "& .label": {
        fontSize: 14,
        fontWeight: 700,
        color: "#949494",
        marginBottom: "10px",
      },
      "& .inputField": {
        backgroundColor: "#ECECEC",
        borderRadius: 0,
      },
    },
  },
  testBox: {
    display: "flex",
    alignItems: "center",
    gap: 19,
    margin: "10px 0px",
  },
  testBtn: {
    minWidth: "80px",
    margin: 0,
    height: "40px",
  },
  testMsg: {
    color: "#333333",
    fontSize: 21,
    fontWeight: 400,
  },
}));

export {
  createBillingValuesObj,
  prepareBillingDataForRequest,
  prepareBillingDataFromResponse,
  useStylesForForms,
};
